import type { ChatHistoryMessage } from "@globals";
import type { ChatMessage } from "@lib/agent/types";

// System-event notices are persisted as user-role rows with this prefix (mirrors
// `SYSTEM_NOTICE_PREFIX` in `apps/agent_proxy/service.py`). They are agent-only context
// and must never render in the chat, so they're filtered out at the seed point.
const SYSTEM_MESSAGE_PREFIX = "[SYSTEM] ";

type ContentBlock = { text?: string };

function flattenContent(content: ChatHistoryMessage["content"]): string {
  if (!Array.isArray(content)) {
    return "";
  }
  return (content as ContentBlock[]).map(block => block?.text ?? "").join("");
}

export function translateBackendMessages(messages: ChatHistoryMessage[]): ChatMessage[] {
  // Strands persists every turn, including `role: "user"` messages whose content is purely
  // `toolResult` blocks and assistant turns that are only `toolUse`. Those have no text and
  // would render as empty chat bubbles, so drop them — tool activity is already conveyed via
  // the streaming `agent.tool_use` path on the live chat side. A message carrying only an
  // attachment (no text) is still meaningful, so it survives the drop on the attachment count.
  return messages.flatMap(m => {
    const content = flattenContent(m.content);
    const attachments = (m.attachments ?? []).map(a => ({ filename: a.filename }));
    if (!content && attachments.length === 0) {
      return [];
    }
    if (m.role === "user" && content.startsWith(SYSTEM_MESSAGE_PREFIX)) {
      return [];
    }
    return [
      {
        id: `hist-${m.message_index}`,
        role: m.role === "assistant" ? "assistant" : "user",
        content,
        attachments: attachments.length > 0 ? attachments : undefined
      }
    ];
  });
}
